import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getWebhookSubscriptions,
  deleteWebhookSubscription,
  testWebhookSubscription,
  WEBHOOK_EVENT_TYPES,
  type WebhookSubscription,
  type WebhookSubscriptionFilters,
} from '@/api/integrationApi';
import { useAuth } from '@/hooks/useAuth';
import IntegrationNav from './IntegrationNav';

export default function WebhookSubscriptionListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [eventTypeFilter, setEventTypeFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: WebhookSubscriptionFilters = { ...params };
      if (eventTypeFilter) filters.event_type = eventTypeFilter;
      return getWebhookSubscriptions(filters);
    },
    [eventTypeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('webhook.confirm_delete_subscription'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteWebhookSubscription(id);
            message.success(t('webhook.subscription_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleTest = useCallback(
    async (id: string) => {
      try {
        const result = await testWebhookSubscription(id);
        if (result.success) {
          message.success(t('webhook.test_success'));
        } else {
          message.error(t('webhook.test_failed', { error: result.error || '' }));
        }
      } catch {
        message.error(t('webhook.test_failed', { error: '' }));
      }
    },
    [t]
  );

  const columns: ColumnsType<WebhookSubscription> = useMemo(
    () => [
      {
        title: t('webhook.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('webhook.field.target_url'),
        dataIndex: 'target_url',
        key: 'target_url',
        width: 220,
        ellipsis: true,
      },
      {
        title: t('webhook.field.event_types'),
        dataIndex: 'event_types',
        key: 'event_types',
        width: 200,
        ellipsis: true,
        render: (v: string[]) => v?.map((et) => (
          <Tag key={et} style={{ marginBottom: 2 }}>{et}</Tag>
        )),
      },
      {
        title: t('webhook.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('webhook.field.failure_count'),
        dataIndex: 'failure_count',
        key: 'failure_count',
        width: 80,
        render: (v: number) => (
          <Tag color={v > 0 ? 'red' : 'default'}>{v}</Tag>
        ),
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 150,
        render: (_: unknown, record: WebhookSubscription) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<ThunderboltOutlined />}
                onClick={() => handleTest(record.id)}
                title={t('webhook.test_send')}
              />
            )}
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/integration/webhooks/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleTest]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('webhook.field.event_types')}
        value={eventTypeFilter}
        onChange={(v) => { setEventTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 220 }}
        options={WEBHOOK_EVENT_TYPES.filter((et) => et !== '*').map((et) => ({
          value: et,
          label: et,
        }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/integration/webhooks/new')}
        >
          {t('webhook.create_subscription_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <IntegrationNav />
      <h2>{t('integration.webhooks_title')}</h2>
      <DataGrid<WebhookSubscription>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('webhook.search_subscriptions_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
